import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { EmptyState } from "../components/ui/EmptyState";
import { useMe } from "../features/auth/useMe";
import { useUnverifiedStations, useVerifyFromQueue } from "../features/admin/hooks";
import { type StationOut } from "../features/stations/api";

export function AdminVerifyQueueRoute() {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.role === "admin";
  const query = useUnverifiedStations(isAdmin);

  if (me.isLoading) {
    return <p className="px-4 py-8 text-fg-mute">{t("common.loading")}</p>;
  }
  if (!isAdmin) {
    return (
      <div className="pt-3">
        <EmptyState tone="ember" glyph="⚠" title={t("admin.not_authorized")} />
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-3">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-fg">{t("admin.queue_title")}</h1>
      </header>

      {query.isLoading && <p className="text-fg-mute">{t("common.loading")}</p>}
      {query.isError && <p className="text-ember">{t("admin.queue_load_failed")}</p>}
      {query.data && query.data.length === 0 && (
        <EmptyState glyph="✓" title={t("admin.queue_empty")} />
      )}
      {query.data && query.data.length > 0 && (
        <ul className="space-y-2">
          {query.data.map((station) => (
            <QueueRow key={station.id} station={station} />
          ))}
        </ul>
      )}
    </div>
  );
}

function QueueRow({ station }: { station: StationOut }) {
  const { t } = useTranslation();
  const verify = useVerifyFromQueue();

  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface p-4">
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-fg">{station.name}</p>
        {station.address && <p className="truncate text-xs text-fg-mute">{station.address}</p>}
        <p className="text-xs text-fg-faint">
          {new Date(station.created_at).toLocaleDateString()}
        </p>
        {verify.isError && <p className="mt-1 text-xs text-ember">{t("admin.verify_failed")}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Link
          to={`/stations/${station.id}`}
          className="rounded-md border border-line px-3 py-1 text-sm text-fg-mute hover:text-fg"
        >
          {t("admin.queue_open")}
        </Link>
        <button
          type="button"
          onClick={() => verify.mutate(station.id)}
          disabled={verify.isPending}
          className="rounded-md bg-volt-dark px-3 py-1 text-sm font-medium text-fg hover:bg-volt-deep disabled:opacity-50"
        >
          {t("admin.verify_station")}
        </button>
      </div>
    </li>
  );
}
